import { useGlobalData } from "@/app/global_data"
import { mainLogger as log } from "@/app/logger"
import { getMembers } from "@/app/metric_utils"
import { useAppData } from "@/app/providers/app_data.provider"
import { Tray, useTray } from "@/app/providers/tray_provider"
import { Metric, Term } from "@/app/types"
import { FEATURE_FLAG_KEYS } from "@/app/types/flag_definitions"
import { Button } from "@/components/buttons/button"
import { FilteredMetricsButton } from "@/components/buttons/button.filtered_metrics_button"
import { RankingTermSelectorButton } from "@/components/buttons/term_selector/button.ranking_term_selector"
import { ComparisonGroupTable } from "@/components/comparison_group_list/table/table"
import { ComparisonGroupListTrayProps } from "@/components/comparison_group_list/types"
import { AsOfDate } from "@/components/layouts/asof_date_text"
import { FilterCurrentStatus } from "@/components/layouts/filter_current_status"
import { useGrowthBook } from "@growthbook/growthbook-react"
import { useRouter } from "next/router"
import { useMemo, useState } from "react"

export const SORT_INITIAL_STATE = {
  column: "value",
  direction: "desc",
  isSorted: false,
}

const CUSTOM_LIST_BASE_URL_PATH = "/lists/peers"

export function ComparisonGroupListTray({
  entity,
  comparison,
  entityValues,
  allValues,
  chart,
}: ComparisonGroupListTrayProps) {
  const { term } = useGlobalData().get()
  const { lookup, metaTerms } = useAppData()
  const { push } = useRouter()
  const { showTray } = useTray()
  const growthbook = useGrowthBook()

  const [selectedTerm, setSelectedTerm] = useState<Term>(term)
  const [showDelta, setShowDelta] = useState(false)

  const comparisonTerm = useMemo(() => {
    const newTerm =
      selectedTerm.isPeriodic ?
        lookup.getTerm(
          false,
          selectedTerm.interval,
          metaTerms.ranking[term.interval][0].key
        ) // always get latest ranking term when viewing from periodic chart
      : selectedTerm

    if (newTerm) setSelectedTerm(newTerm)

    return newTerm
  }, [selectedTerm])

  if (!comparisonTerm) {
    log.warn(term.isPeriodic, term, term.interval, term.key, {
      Entity: entity,
      Comparison: comparison,
      "Entity Ranking Values": entityValues?.ranking,
      "All Ranking Values": allValues[comparison.getCalculation()].ranking,
    })
  }

  const [metric, setMetric] = useState<Metric>(
    chart.chartMetrics[0].name !== Metric.CumeNetflow ?
      chart.chartMetrics[0]
    : Metric.getMetric(Metric.Netflow) // Cumulative Netflows turns into Netflows
  )

  const [sortOrder, setSortOrder] = useState(SORT_INITIAL_STATE)

  // if there is a list key, then the custom list is currently selected
  const listKey = comparison.isCustomList() ? comparison.getGroup() : undefined

  const comparisonTrayTitle = useMemo(() => {
    if (comparison.isPeers()) return entity.getName()

    if (comparison.isTop10())
      return (
        comparison.getGroupName(entity.isFund()) +
        ` ${entity.isFund() ? "Funds" : "Firms"} by ${metric.text}`
      )

    if (comparison.isCustomList())
      return comparison.getGroupName(entity.isFund())

    return entity.isFund() ?
        `${entity.getFund().category} category`
      : comparison.getGroupName(entity.isFund())
  }, [comparison, entity, metric])

  // URL path for custom list creation or editing
  const customListParamsURL =
    listKey ? `/edit/${listKey}` : `/${entity.isFund() ? "fund" : "firm"}/new`

  // custom peers button (either to create or edit custom list) should only be displayed when peers or custom list is currently selected
  const shouldDisplayCustomPeersButton =
    (comparison.isPeers() || comparison.isCustomList()) &&
    growthbook.isOn(FEATURE_FLAG_KEYS.features.SHOW_CUSTOM_LIST)

  const comparisonMembers = useMemo(() => {
    const newMembers = getMembers(
      entity,
      comparison,
      entityValues,
      allValues,
      chart,
      selectedTerm
    )
    return newMembers
  }, [selectedTerm])

  return (
    <Tray>
      <div className="flex flex-col gap-0.5 pl-2 font-semibold text-main-600 sm:gap-1">
        <div
          className={`w-full pb-2 text-base sm:text-xl ${comparison.isPeers() ? "text-primary-500" : "text-main-dark"} `}>
          {comparisonTrayTitle}
        </div>
        <div className="flex justify-between">
          <div className="flex flex-col justify-end">
            <FilterCurrentStatus className="h-auto sm-mobile:leading-5" />
            <div className="flex flex-col gap-2">
              <div>
                <Button
                  onClick={() => setShowDelta(!showDelta)}
                  color={showDelta ? "primary" : "tertiary"}>
                  Show Delta
                </Button>
              </div>
              {shouldDisplayCustomPeersButton ?
                <div className="">
                  <Button
                    onClick={() => {
                      push(CUSTOM_LIST_BASE_URL_PATH + customListParamsURL)
                      showTray(null)
                    }}
                    color="secondary">
                    {listKey ? "Edit List" : "Customize Peers"}
                  </Button>
                </div>
              : null}
            </div>
          </div>

          <div className="flex flex-col justify-end gap-2">
            <RankingTermSelectorButton
              isTermRelevant={metric.isTermDurationRelevant}
              onClick={(t) => setSelectedTerm(t)}
              term={selectedTerm}
            />
            <FilteredMetricsButton
              isFund={entity.isFund()}
              metric={metric}
              setMetric={setMetric}
              entityValues={entityValues}
              term={selectedTerm}
            />
          </div>
        </div>
      </div>

      <ComparisonGroupTable
        comparison={comparison}
        isFund={entity.isFund()}
        term={selectedTerm}
        members={comparisonMembers}
        metric={metric}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        showDelta={showDelta}
      />
      <div className="px-4 py-1">
        <AsOfDate term={selectedTerm} />
      </div>
    </Tray>
  )
}
